import { useEffect } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import {
  FaBolt,
  FaCloud,
  FaCode,
  FaCogs,
  FaDatabase,
  FaGraduationCap,
  FaHeadset,
  FaHeadphonesAlt,
  FaPlug,
  FaRobot,
  FaVial,
} from 'react-icons/fa';
import {
  findService,
  getPracticeForService,
  getServiceSeo,
  practicePath,
} from '../../content/services';
import { ServiceIconKey } from '../../content/types';
import Breadcrumbs from '../../components/Breadcrumbs/Breadcrumbs';
import CTASection from '../../components/CTASection/CTASection';
import CertificationBadges from '../../components/CertificationBadges/CertificationBadges';
import ProofBar from '../../components/ProofBar/ProofBar';
import Seo, { seoPropsFor } from '../../components/Seo/Seo';
import { SHOW_CERTIFICATIONS, getCertificationByCode } from '../../content/certifications';
import styles from './ServiceDetail.module.scss';

export const SERVICE_ICONS: Record<ServiceIconKey, React.ComponentType<any>> = {
  itsm: FaCogs,
  cmdb: FaDatabase,
  csm: FaHeadphonesAlt,
  automation: FaPlug,
  customDev: FaCode,
  testing: FaVial,
  training: FaGraduationCap,
  // Deliberately a generic cloud mark, not FaAws: the repo holds no AWS
  // certification, and an AWS logo on the card reads as one.
  cloud: FaCloud,
  serverless: FaBolt,
  ai: FaRobot,
  hosting: FaHeadset,
};

/**
 * One template for all eleven services: hero, problem, certification,
 * engagement types, FAQ, proof slot, CTA.
 *
 * The order is the argument. The problem comes before anything we sell, and
 * the certification sits immediately next to it -- proof beside the claim it
 * supports rather than in a strip at the bottom of the page.
 */
const ServiceDetailPage = () => {
  const { practiceSlug, serviceSlug } = useParams<{
    practiceSlug: string;
    serviceSlug: string;
  }>();

  // Resolved through the practice segment, so a service is never served from
  // a URL under a practice it does not belong to.
  const service = findService(practiceSlug, serviceSlug);

  useEffect(() => {
    window.scrollTo({ top: 0 });
  }, [practiceSlug, serviceSlug]);

  if (!service) {
    return <Navigate to="/services" replace />;
  }

  const practice = getPracticeForService(service);
  const Icon = SERVICE_ICONS[service.iconKey];
  // Switched off site-wide right now, so no badge renders beside any problem
  // statement. See content/certifications.ts.
  const certification =
    SHOW_CERTIFICATIONS && service.certification
      ? getCertificationByCode(service.certification)
      : undefined;

  return (
    <div className={styles.serviceDetailPage}>
      {/* Title, description, and canonical come from the service itself --
          there is no per-service entry in PAGE_SEO to keep in sync. */}
      <Seo {...seoPropsFor(getServiceSeo(service))} />

      <div className="main-content">
        <section className={styles.hero}>
          <div className={styles.container}>
            <Breadcrumbs
              items={[
                { label: 'Services', href: '/services' },
                ...(practice ? [{ label: practice.name, href: practicePath(practice) }] : []),
                { label: service.navLabel },
              ]}
            />
            <h1>
              <Icon className={styles.icon} aria-hidden="true" />
              {service.title}
            </h1>
            <p className={styles.summary}>{service.summary}</p>
          </div>
        </section>

        <section className={styles.problem}>
          <div className={styles.container}>
            <div className={styles.problemBody}>
              <p>{service.problem}</p>

              {/* Inline, adjacent to the problem statement: proof next to the
                  claim it supports, when certification display is on. */}
              {certification && (
                <CertificationBadges certifications={[certification]} label="Backed by" />
              )}
            </div>
          </div>
        </section>

        {/* The services offered within this platform area. Titled as
            offerings rather than as an engagement model, because that is how
            a buyer reads them. */}
        <section className={styles.engagements}>
          <div className={styles.container}>
            <h2>Services We Offer</h2>
            <div className={styles.engagementGrid}>
              {service.engagements.map((engagement) => (
                <article key={engagement.name} className={styles.engagementCard}>
                  <h3>{engagement.name}</h3>
                  <p>{engagement.description}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* The "What's Included" capability list is not rendered. The data is
            still on each Service -- it is the record of which ServiceNow
            modules the taxonomy rebuild kept, moved, or cut, and
            content/services.test.ts enforces those placements. It is simply
            not shown on the page any more. */}

        <section className={styles.faqs}>
          <div className={styles.container}>
            <h2>Frequently Asked Questions</h2>
            <dl className={styles.faqList}>
              {service.faqs.map((faq) => (
                <div key={faq.question} className={styles.faqItem}>
                  <dt>{faq.question}</dt>
                  <dd>{faq.answer}</dd>
                </div>
              ))}
            </dl>
          </div>
        </section>

        {/* Proof slot. Null on all eleven services, so nothing renders --
            no section element, no empty band, no "case study coming soon". */}
        {service.proofSlot && (
          <section className={styles.proofSection}>
            <div className={styles.container}>
              <ProofBar
                items={[{ value: service.proofSlot.stat, label: service.proofSlot.context }]}
              />
            </div>
          </section>
        )}
      </div>

      <CTASection
        heading={`Discuss ${service.navLabel} with our team`}
        body="Tell us about your environment and what you need to achieve. We will outline what the work involves and where it starts."
        ctaLabel="Contact Us"
        ctaHref="/contact"
      />
    </div>
  );
};

export default ServiceDetailPage;
